import React, { useState, useEffect } from "react";
import { db } from "../../config/firebase";
import {
  collection,
  query,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
} from "firebase/firestore";
import AdminGuard from "../../components/AdminGuard";
import JobPostingForm from "../../components/admin/JobPostingForm";

const AdminDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const jobsQuery = query(
        collection(db, "jobs"),
        orderBy("postedAt", "desc")
      );
      const querySnapshot = await getDocs(jobsQuery);
      const jobsList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setJobs(jobsList);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm("Are you sure you want to delete this job?")) {
      try {
        await deleteDoc(doc(db, "jobs", jobId));
        setJobs(jobs.filter((job) => job.id !== jobId));
      } catch (error) {
        console.error("Error deleting job:", error);
      }
    }
  };

  return (
    <AdminGuard>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {showForm ? "Hide Form" : "Post New Job"}
          </button>
        </div>

        {showForm && (
          <div className="mb-8">
            <JobPostingForm />
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <h2 className="text-xl font-semibold p-6 border-b">Posted Jobs</h2>

          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : jobs.length === 0 ? (
            <p className="p-6 text-center text-gray-500">No jobs posted yet.</p>
          ) : (
            <div className="divide-y">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="p-6 hover:bg-gray-50 cursor-pointer border-b"
                  onClick={() => (window.location.href = `/jobs/${job.id}`)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">{job.title}</h3>
                      <p className="text-gray-600">{job.company}</p>
                      <p className="text-sm text-gray-500">
                        Posted on {new Date(job.postedAt).toLocaleDateString()}
                      </p>
                      <div className="mt-2 space-x-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                          {job.type}
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                          {job.experience}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteJob(job.id);
                        }}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>

                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          const url = `${window.location.origin}/jobs/${job.id}`;
                          try {
                            if (navigator.share) {
                              await navigator.share({ title: job.title, url });
                            } else {
                              await navigator.clipboard.writeText(url);
                              alert("Job link copied to clipboard!");
                            }
                          } catch (err) {
                            console.error("Error sharing/copying:", err);
                            alert("Failed to share or copy the link.");
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminGuard>
  );
};

export default AdminDashboard;
